import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Menu, MenuItem,
  Divider, Tooltip, Alert, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, Grid,
  Autocomplete, SelectChangeEvent, List, ListItem, ListItemText,
  ListItemSecondaryAction, TableSortLabel, Popover, Checkbox, FormControlLabel, FormGroup, Link,
} from '@mui/material';
import { Refresh, Warning, Add, Login, Logout, Edit, Delete, ArrowDropDown, Place, MergeType, Gavel, QuestionAnswer, Clear, Schedule, FilterList, OpenInNew, Description } from '@mui/icons-material';
import CaseEntryPanel from '../../components/CaseEntryPanel';
import CallTimesPanel from '../../components/CallTimesPanel';
import { useAuth } from '../../contexts/AuthContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../api/client';
import useCADWebSocket, { SoundConfig } from '../../hooks/useCADWebSocket';
import { playSound } from '../../utils/sounds';
import { formatTime24, formatDateShort, ensureTimezone } from '../../utils/time';

const LOCATION_SOURCE_LABELS: Record<string, string> = {
  '911-pidf': 'E911 (NG911 location)',
  rapidsos: 'RapidSOS',
  device: 'device GPS',
  civic: 'E911 civic address',
  billing: 'billing address',
  premise: 'saved premise record',
  simulated: 'simulated 911 (test)',
};

interface UnitRecord {
  id: number; callsign: string; unit_type: string; unit_type_icon?: string; agency_name: string;
  status_id: number | null; status_name: string; status_color: string;
  assigned_call_id: number | null; assigned_call_cad_number: string | null;
  notes: string; updated_at: string; status_code?: string;
  is_logged_on?: boolean; current_location?: string;
  transport_destination?: string;
  location_updated_at?: string;
  location_lat?: number | null; location_lng?: number | null;
}

interface CallRecord {
  id: number; cad_number: string; case_number: string; call_type_id: number;
  call_type_code: string; call_type_name: string;
  priority: number; status: string; location_address: string;
  location_lat?: number | null; location_lng?: number | null;
  location_apt?: string; location_city?: string; location_state?: string; cross_street?: string;
  reporting_party: string; reporting_phone?: string; source?: string;
  narrative: string; agency_id?: number; agency_name: string;
  created_at: string;
  call_type_status_check: boolean; call_type_status_check_interval: number;
  location_id?: number | null; premise_documents?: PremiseDocument[];
}

interface PremiseDocument {
  id: number; category: string; notes: string; original_filename: string;
  content_type: string; size_bytes: number; uploaded_by: string;
  uploaded_at: string; view_url: string;
}

export const DOC_CATEGORY_LABELS: Record<string, string> = {
  building_diagram: 'Building Diagram',
  hazard_plan: 'Hazard Plan',
  emergency_contacts: 'Emergency Contacts',
  other: 'Other',
};

interface StatusType {
  id: number; code: string; name: string; color: string; is_available: boolean;
}

interface StatusCheckAlert {
  callId: number;
  cadNumber: string;
  callType: string;
  units: string[];
  minutesOverdue: number;
}

interface CallType { id: number; code: string; name: string; priority: number; }
interface Agency { id: number; name: string; }
interface DispositionOption { id: number; code: string; name: string; }
interface PersonnelOption { id: number; dsn: string; first_name: string; last_name: string; }
interface UnitPersonnelEntry { personnel_id: number; dsn: string; first_name: string; last_name: string; role: string; }
interface CallPriorityOption { id: number; code: number; name: string; color: string; sort_order: number; }
interface LocationResult {
  id: number; name: string; category: string; address: string; apt_suite: string;
  city: string; state: string; zip_code: string; latitude: number | null;
  longitude: number | null; hazard_info: string; additional_notes: string;
}

const PRIORITY_COLORS: Record<number, string> = { 1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e' };

const DENSE_CELL = { py: 0.25, px: 1, fontSize: 13, lineHeight: 1.3 } as const;
const DENSE_HEADER = { ...DENSE_CELL, fontWeight: 700 } as const;

export default function UnitsCallsPage() {
  const { user } = useAuth();
  const isSuperadmin = !!user && (user.role === 'superadmin' || user.superadmin_access);
  const [units, setUnits] = useState<UnitRecord[]>([]);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [statusTypes, setStatusTypes] = useState<StatusType[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUnit, setSelectedUnit] = useState<UnitRecord | null>(null);
  const [statusCheckAlerts, setStatusCheckAlerts] = useState<StatusCheckAlert[]>([]);
  const [dismissedAlerts, setDismissedAlerts] = useState<Map<string, number>>(new Map());
  const loggedAlertsRef = useRef<Set<string>>(new Set());
  const lastAlertSoundRef = useRef<number>(0);
  const [statusCheckSound, setStatusCheckSound] = useState('urgent');
  const [pendingCallAlertSeconds, setPendingCallAlertSeconds] = useState(60);
  const [pendingCallAlertSound, setPendingCallAlertSound] = useState('urgent');
  const [pendingCallIds, setPendingCallIds] = useState<Set<number>>(new Set());
  const lastPendingAlertSoundRef = useRef<number>(0);
  const [logonAnchor, setLogonAnchor] = useState<null | HTMLElement>(null);

  // Unit sort state
  type UnitSortKey = 'callsign' | 'unit_type' | 'agency_name';
  const [unitSortBy, setUnitSortBy] = useState<UnitSortKey>('callsign');
  const [unitSortDir, setUnitSortDir] = useState<'asc' | 'desc'>('asc');
  // Per-session agency filter for the Unit Status grid (empty = show all).
  // Intentionally NOT persisted, so it resets to "all" on every login/reload.
  const [unitAgencyFilter, setUnitAgencyFilter] = useState<string[]>([]);
  const [agencyFilterAnchor, setAgencyFilterAnchor] = useState<null | HTMLElement>(null);
  const handleUnitSort = (key: UnitSortKey) => {
    if (unitSortBy === key) {
      setUnitSortDir(unitSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setUnitSortBy(key);
      setUnitSortDir('asc');
    }
  };

  // Edit Unit (personnel) dialog state
  const [editUnitOpen, setEditUnitOpen] = useState(false);
  const [editUnitTarget, setEditUnitTarget] = useState<UnitRecord | null>(null);
  const [unitPersonnel, setUnitPersonnel] = useState<UnitPersonnelEntry[]>([]);
  const [allPersonnel, setAllPersonnel] = useState<PersonnelOption[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<PersonnelOption | null>(null);

  // New Call dialog state
  const [newCallOpen, setNewCallOpen] = useState(false);
  // Set when an inbound call auto-fills an address with no coordinates, so we
  // geocode it once the dialog is open (e.g. a saved premise record).
  const [pendingPhoneGeocode, setPendingPhoneGeocode] = useState(false);
  const [caseEntryOpen, setCaseEntryOpen] = useState(false);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [callPriorities, setCallPriorities] = useState<CallPriorityOption[]>([]);
  const [dispositions, setDispositions] = useState<DispositionOption[]>([]);
  const [locationResults, setLocationResults] = useState<LocationResult[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationResult | null>(null);
  const locationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewCoords, setPreviewCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAreaInfo, setPreviewAreaInfo] = useState('');
  const previewMapRef = useRef<HTMLDivElement>(null);
  const previewMapInstance = useRef<any>(null);
  const previewMarkerRef = useRef<any>(null);
  const locationInputRef = useRef<HTMLInputElement | null>(null);
  const [callForm, setCallForm] = useState({
    call_type_id: 0, priority: 3, location_address: '', location_lat: null as number | null,
    location_lng: null as number | null, location_apt: '', location_city: '', location_state: '', cross_street: '',
    reporting_party: '', reporting_phone: '', source: '', location_source: '', narrative: '', agency_id: null as number | null,
    location_id: null as number | null,
  });

  // Schedule Call dialog state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleAdvance, setScheduleAdvance] = useState(30);

  const loadAll = useCallback(() => {
    api.get('/units').then((r) => setUnits(r.data));
    api.get('/calls/pending').then((r) => setCalls(r.data));
    api.get('/status-types').then((r) => setStatusTypes(r.data));
  }, []);

  const [soundConfig, setSoundConfig] = useState<SoundConfig>({});

  const [tzReady, setTzReady] = useState(false);
  const [jurisdictionState, setJurisdictionState] = useState('');
  const [jurisdictionCounty, setJurisdictionCounty] = useState('');
  const [defaultCity, setDefaultCity] = useState('');
  const [defaultState, setDefaultState] = useState('');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 37.2, lng: -93.3 });

  useEffect(() => {
    ensureTimezone().then(() => setTzReady(true));
    api.get('/system-config/public').then((r) => {
      setSoundConfig({
        sound_dispatch_new_call: r.data.sound_dispatch_new_call,
        sound_dispatch_status_change: r.data.sound_dispatch_status_change,
      });
      if (r.data.sound_dispatch_status_check) {
        setStatusCheckSound(r.data.sound_dispatch_status_check);
      }
      if (r.data.pending_call_alert_seconds) setPendingCallAlertSeconds(r.data.pending_call_alert_seconds);
      if (r.data.sound_pending_call_alert) setPendingCallAlertSound(r.data.sound_pending_call_alert);
      if (r.data.sound_dispatch_emergency) emergencySoundRef.current = r.data.sound_dispatch_emergency;
      if (r.data.jurisdiction_state) setJurisdictionState(r.data.jurisdiction_state);
      if (r.data.jurisdiction_county) setJurisdictionCounty(r.data.jurisdiction_county);
      if (r.data.default_city) setDefaultCity(r.data.default_city);
      if (r.data.default_state) setDefaultState(r.data.default_state);
      if (r.data.map_center_lat && r.data.map_center_lng) setMapCenter({ lat: r.data.map_center_lat, lng: r.data.map_center_lng });
    }).catch(() => { setTzReady(true); });
  }, []);

  const [emergencyAlert, setEmergencyAlert] = useState<{ callsign: string; personnel: string; location_lat?: number; location_lng?: number; location_updated_at?: string; location_address?: string } | null>(null);
  const emergencySoundRef = useRef('urgent');

  const handleWsEvent = useCallback((event: string, data: any) => {
    if (event === 'unit_emergency') {
      setEmergencyAlert({ callsign: data.callsign, personnel: data.personnel || '', location_lat: data.location_lat, location_lng: data.location_lng, location_updated_at: data.location_updated_at, location_address: data.location_address || '' });
      if (emergencySoundRef.current && emergencySoundRef.current !== 'none') playSound(emergencySoundRef.current);
    } else if (event === 'unit_emergency_cancel') {
      setEmergencyAlert(null);
    } else if (event === 'phone_call_received') {
      const posId = localStorage.getItem('operator_position_id');
      if (posId && String(data?.operator_position_id) === posId) {
        const hasLoc = data.location_address || (data.location_lat && data.location_lng);
        setCallForm((prev) => ({
          ...prev,
          reporting_party: data.caller_id_name || '',
          reporting_phone: data.caller_id_num || '',
          source: data.source || 'Admin',
          location_address: data.location_address || prev.location_address,
          location_apt: data.location_apt || prev.location_apt,
          location_lat: data.location_lat ?? prev.location_lat,
          location_lng: data.location_lng ?? prev.location_lng,
          location_source: data.location_source || '',
        }));
        setNewCallOpen(true);
        // If E911/ALI provided coordinates, drop the map pin. If we only got an
        // address (e.g. a saved premise record without coordinates), geocode it
        // so the pin still drops. Otherwise focus Location for manual entry.
        if (data.location_lat && data.location_lng) {
          setTimeout(() => geocodeCoords(data.location_lat, data.location_lng), 350);
        } else {
          if (hasLoc && data.location_address) setPendingPhoneGeocode(true);
          setTimeout(() => locationInputRef.current?.focus(), 350);
        }
      }
    }
  }, []);

  useCADWebSocket(loadAll, 'dispatch', soundConfig, handleWsEvent);

  // Geocode an auto-filled premise/E911 address that arrived without coordinates.
  useEffect(() => {
    if (pendingPhoneGeocode && newCallOpen && callForm.location_address &&
        !(callForm.location_lat && callForm.location_lng)) {
      setPendingPhoneGeocode(false);
      handlePreviewAddress();
    }
  }, [pendingPhoneGeocode, newCallOpen, callForm.location_address, callForm.location_lat, callForm.location_lng]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill City/State with the agency defaults when the New Call form opens.
  useEffect(() => {
    if (newCallOpen) {
      setCallForm((f) => ({
        ...f,
        location_city: f.location_city || defaultCity,
        location_state: f.location_state || defaultState,
      }));
    }
  }, [newCallOpen, defaultCity, defaultState]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tzReady) return;
    loadAll();
    api.get('/call-types').then((r) => setCallTypes(r.data));
    api.get('/agencies').then((r) => setAgencies(r.data));
    api.get('/call-priorities').then((r) => setCallPriorities(r.data));
    api.get('/dispositions').then((r) => setDispositions(r.data)).catch(() => {});
  }, [loadAll, tzReady]);

  // Status check reminder logic
  useEffect(() => {
    const checkStatusAlerts = () => {
      const now = Date.now();
      const alerts: StatusCheckAlert[] = [];

      const callMap = new Map<number, CallRecord>();
      calls.forEach((c) => {
        if (c.call_type_status_check && (c.status === 'dispatched' || c.status === 'active')) {
          callMap.set(c.id, c);
        }
      });

      const callUnits = new Map<number, { units: string[]; oldestUpdate: number }>();
      units.forEach((u) => {
        if (
          u.status_name?.toLowerCase() === 'on scene' &&
          u.assigned_call_id &&
          callMap.has(u.assigned_call_id)
        ) {
          const existing = callUnits.get(u.assigned_call_id) || { units: [], oldestUpdate: now };
          existing.units.push(u.callsign);
          const ts = u.updated_at + (u.updated_at.endsWith('Z') ? '' : 'Z');
          const unitUpdate = new Date(ts).getTime();
          if (unitUpdate < existing.oldestUpdate) {
            existing.oldestUpdate = unitUpdate;
          }
          callUnits.set(u.assigned_call_id, existing);
        }
      });

      callUnits.forEach((data, callId) => {
        const call = callMap.get(callId);
        if (!call) return;
        const intervalMs = call.call_type_status_check_interval * 60 * 1000;
        const elapsed = now - data.oldestUpdate;
        if (elapsed >= intervalMs) {
          const dismissedAt = dismissedAlerts.get(`${callId}`);
          if (dismissedAt && (now - dismissedAt) < intervalMs) return;
          const minutesOverdue = Math.floor(elapsed / 60000);
          alerts.push({
            callId,
            cadNumber: call.cad_number,
            callType: `${call.call_type_code} - ${call.call_type_name}`,
            units: data.units,
            minutesOverdue,
          });
        }
      });

      setStatusCheckAlerts(alerts);

      alerts.forEach((a) => {
        const logKey = `${a.callId}-${a.minutesOverdue}`;
        if (!loggedAlertsRef.current.has(logKey)) {
          loggedAlertsRef.current.add(logKey);
          api.post('/calls/status-check/alert', { call_id: a.callId, units: a.units, minutes_on_scene: a.minutesOverdue }).catch(() => {});
        }
      });

      if (alerts.length > 0 && now - lastAlertSoundRef.current > 60000) {
        if (alerts.length > 0) {
          playSound(statusCheckSound);
          lastAlertSoundRef.current = now;
        }
      }
    };

    checkStatusAlerts();
    const id = setInterval(checkStatusAlerts, 30000);
    return () => clearInterval(id);
  }, [units, calls, dismissedAlerts, statusCheckSound]);

  // Pending call (no unit assigned) alert logic
  useEffect(() => {
    if (pendingCallAlertSeconds <= 0) return;
    const checkPendingCalls = () => {
      const now = Date.now();
      const thresholdMs = pendingCallAlertSeconds * 1000;
      const pending = new Set<number>();
      calls.forEach((c) => {
        if (c.status === 'closed' || c.status === 'cancelled') return;
        const hasUnit = units.some((u) => u.assigned_call_id === c.id);
        if (!hasUnit) {
          // Server returns naive UTC timestamps — append Z if no timezone indicator
          const ts = c.created_at.endsWith('Z') || c.created_at.includes('+') ? c.created_at : c.created_at + 'Z';
          const age = now - new Date(ts).getTime();
          if (age >= thresholdMs) pending.add(c.id);
        }
      });
      setPendingCallIds(pending);
      if (pending.size > 0 && now - lastPendingAlertSoundRef.current > 30000) {
        playSound(pendingCallAlertSound);
        lastPendingAlertSoundRef.current = now;
      }
    };
    checkPendingCalls();
    const id = setInterval(checkPendingCalls, 10000);
    return () => clearInterval(id);
  }, [units, calls, pendingCallAlertSeconds, pendingCallAlertSound]);

  const dismissAlert = (callId: number) => {
    setDismissedAlerts((prev) => new Map(prev).set(`${callId}`, Date.now()));
    const alert = statusCheckAlerts.find((a) => a.callId === callId);
    if (alert) {
      api.post('/calls/status-check/ack', { call_id: callId, units: alert.units, minutes_on_scene: alert.minutesOverdue }).catch(() => {});
    }
  };

  const handleStatusClick = (e: React.MouseEvent<HTMLElement>, unit: UnitRecord) => {
    setAnchorEl(e.currentTarget);
    setSelectedUnit(unit);
  };

  // Transport destination state
  const [transportDialogOpen, setTransportDialogOpen] = useState(false);
  const [transportDest, setTransportDest] = useState('');
  const [transportOther, setTransportOther] = useState('');
  const [pendingTransportUnit, setPendingTransportUnit] = useState<UnitRecord | null>(null);
  const [pendingTransportStatusId, setPendingTransportStatusId] = useState<number | null>(null);
  const [destinations, setDestinations] = useState<{ id: number; name: string; address: string; phone: string; notes: string }[]>([]);

  const handleStatusChange = async (statusId: number) => {
    if (!selectedUnit) { setAnchorEl(null); return; }
    // Check if this is the Transporting status
    const st = statusTypes.find((s) => s.id === statusId);
    if (st && st.code === 'TRN') {
      // Open destination picker
      setPendingTransportUnit(selectedUnit);
      setPendingTransportStatusId(statusId);
      setTransportDest('');
      setTransportOther('');
      api.get('/destinations').then((r) => setDestinations(r.data));
      setTransportDialogOpen(true);
      setAnchorEl(null);
      setSelectedUnit(null);
      return;
    }
    await api.patch(`/units/${selectedUnit.id}/status/${statusId}`);
    // If status is an "available" type and unit is on a call, clear them from the call
    if (st && st.is_available && selectedUnit.assigned_call_id) {
      const callId = selectedUnit.assigned_call_id;
      const remainingUnits = units.filter((u) => u.assigned_call_id === callId && u.id !== selectedUnit.id);
      await api.post(`/units/${selectedUnit.id}/clear`);
      if (remainingUnits.length === 0) {
        const call = calls.find((c) => c.id === callId);
        if (call) {
          setCloseCallId(callId);
          setCloseCallCadNumber(call.cad_number);
          setCloseDisposition('');
          setCloseCallPromptOpen(true);
        }
      }
    }
    loadAll();
    setAnchorEl(null);
    setSelectedUnit(null);
  };

  const handleTransportConfirm = async () => {
    if (pendingTransportUnit && pendingTransportStatusId) {
      const dest = transportDest === '__OTHER__' ? transportOther : transportDest;
      await api.patch(`/units/${pendingTransportUnit.id}/status/${pendingTransportStatusId}`, { destination: dest });
      loadAll();
    }
    setTransportDialogOpen(false);
    setPendingTransportUnit(null);
    setPendingTransportStatusId(null);
  };

  const handleLogon = async (unitId: number) => {
    try {
      await api.post(`/units/${unitId}/logon`);
      loadAll();
    } catch { /* ignore */ }
  };

  const handleLogoff = async (unitId: number) => {
    try {
      await api.post(`/units/${unitId}/logoff`);
      loadAll();
    } catch { /* ignore */ }
  };

  const handleEditUnit = async (unit: UnitRecord) => {
    setAnchorEl(null);
    setEditUnitTarget(unit);
    setSelectedPerson(null);
    try {
      const [persRes, allRes] = await Promise.all([
        api.get(`/units/${unit.id}/personnel`),
        api.get('/personnel'),
      ]);
      setUnitPersonnel(persRes.data);
      setAllPersonnel(allRes.data);
    } catch {
      setUnitPersonnel([]);
      setAllPersonnel([]);
    }
    setEditUnitOpen(true);
  };

  const handleAssignPerson = async () => {
    if (!editUnitTarget || !selectedPerson) return;
    await api.post(`/units/${editUnitTarget.id}/assign-personnel/${selectedPerson.id}`);
    setSelectedPerson(null);
    const r = await api.get(`/units/${editUnitTarget.id}/personnel`);
    setUnitPersonnel(r.data);
  };

  const [dispatchCallId, setDispatchCallId] = useState<number | null>(null);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);

  const handleDispatchClick = (callId: number) => {
    setDispatchCallId(callId);
    setDispatchDialogOpen(true);
  };

  const handleDispatchUnit = async (unitId: number) => {
    if (!dispatchCallId) return;
    try {
      await api.post(`/units/${unitId}/dispatch/${dispatchCallId}`);
      loadAll();
    } catch { /* ignore */ }
    setDispatchDialogOpen(false);
    setDispatchCallId(null);
  };

  const [closeCallPromptOpen, setCloseCallPromptOpen] = useState(false);
  const [closeCallId, setCloseCallId] = useState<number | null>(null);
  const [closeCallCadNumber, setCloseCallCadNumber] = useState('');
  const [closeDisposition, setCloseDisposition] = useState('');
  const [callDetailCaseEntryOpen, setCallDetailCaseEntryOpen] = useState(false);

  const handleClearUnit = async (unitId: number) => {
    try {
      await api.post(`/units/${unitId}/clear`);
      // Check if this was the last unit on the call
      const unit = units.find((u) => u.id === unitId);
      if (unit && unit.assigned_call_id) {
        const callId = unit.assigned_call_id;
        const call = calls.find((c) => c.id === callId);
        const remainingUnits = units.filter((u) => u.assigned_call_id === callId && u.id !== unitId);
        if (remainingUnits.length === 0 && call) {
          setCloseCallId(callId);
          setCloseCallCadNumber(call.cad_number);
          setCloseDisposition('');
          setCloseCallPromptOpen(true);
        }
      }
      loadAll();
    } catch { /* ignore */ }
  };

  const handleCloseCall = async () => {
    if (!closeCallId) return;
    try {
      await api.patch(`/calls/${closeCallId}`, { status: 'closed', disposition: closeDisposition });
      loadAll();
      // Also close the call detail dialog if this call was open
      if (callDetail && callDetail.id === closeCallId) {
        setCallDetailOpen(false);
        setCallEditMode(false);
      }
    } catch { /* ignore */ }
    setCloseCallPromptOpen(false);
    setCloseCallId(null);
  };

  const handleLeavePending = () => {
    setCloseCallPromptOpen(false);
    setCloseCallId(null);
  };

  const [callDetailOpen, setCallDetailOpen] = useState(false);
  const [callDetail, setCallDetail] = useState<CallRecord | null>(null);
  const [newNote, setNewNote] = useState('');
  const [locationHistory, setLocationHistory] = useState<any[]>([]);
  const [callEditMode, setCallEditMode] = useState(false);
  const [callEditForm, setCallEditForm] = useState({
    call_type_id: 0, priority: 3, location_address: '', location_apt: '',
    location_city: '', location_state: '',
    cross_street: '', reporting_party: '', reporting_phone: '', source: '', agency_id: null as number | null,
    status: '',
  });

  // Merge call state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);

  const handleMergeCall = async () => {
    if (!callDetail || !mergeTargetId) return;
    try {
      const r = await api.post(`/calls/${mergeTargetId}/merge`, { secondary_call_id: callDetail.id });
      setCallDetail(r.data);
      setMergeDialogOpen(false);
      setMergeTargetId(null);
      loadAll();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Merge failed');
    }
  };

  const handleCallClick = async (callId: number) => {
    try {
      const r = await api.get(`/calls/${callId}`);
      setCallDetail(r.data);
      setNewNote('');
      setCallEditMode(false);
      setCallDetailCaseEntryOpen(false);
      setCallDetailOpen(true);
      if (r.data.location_address) {
        api.get('/calls/location-history', { params: { address: r.data.location_address, exclude_id: callId, limit: 10 } })
          .then((hr) => setLocationHistory(hr.data))
          .catch(() => setLocationHistory([]));
      } else {
        setLocationHistory([]);
      }
    } catch { /* ignore */ }
  };

  const handleHistoryCallClick = async (callId: number) => {
    try {
      let data: any;
      try { data = (await api.get(`/calls/${callId}/detail`)).data; }
      catch { data = (await api.get(`/calls/${callId}`)).data; }
      const ts = (d: string) => d ? new Date(d.endsWith?.('Z') ? d : d + 'Z').toLocaleString() : '—';
      const html = `<!DOCTYPE html><html><head><title>${data.cad_number} — Call Detail</title><style>
        body{font-family:system-ui,sans-serif;background:#1a1a2e;color:#eee;padding:20px;margin:0}
        h2{margin:0 0 8px;color:#fff}
        .badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:700;background:#333;margin-left:8px}
        .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin:16px 0}
        .field label{display:block;font-size:11px;color:#999;text-transform:uppercase}
        .field span{font-size:14px}
        .narrative{background:#111;border:1px solid #333;border-radius:6px;padding:12px;white-space:pre-wrap;font-family:monospace;font-size:13px;max-height:300px;overflow:auto;margin-top:12px}
        </style></head><body>
        <h2>${data.cad_number} — ${data.call_type_code || ''} ${data.call_type_name || ''}<span class="badge">${data.status}</span></h2>
        <div class="grid">
          <div class="field"><label>Address</label><span>${data.location_address || '—'}</span></div>
          <div class="field"><label>City / State</label><span>${[data.location_city, data.location_state].filter(Boolean).join(', ') || '—'}</span></div>
          <div class="field"><label>Priority</label><span>P${data.priority}</span></div>
          <div class="field"><label>Agency</label><span>${data.agency_name || '—'}</span></div>
          <div class="field"><label>Reporting Party</label><span>${data.reporting_party || '—'}</span></div>
          <div class="field"><label>Phone</label><span>${data.reporting_phone || '—'}</span></div>
          <div class="field"><label>Source</label><span>${(data as any).source || '—'}</span></div>
          <div class="field"><label>Disposition</label><span>${data.disposition || '—'}</span></div>
          <div class="field"><label>Created</label><span>${ts(data.created_at)}</span></div>
          <div class="field"><label>Closed</label><span>${ts(data.closed_at)}</span></div>
          <div class="field"><label>Case #</label><span>${data.case_number || '—'}</span></div>
        </div>
        <h3 style="margin:16px 0 6px;font-size:14px;color:#aaa">Narrative</h3>
        <div class="narrative">${(data.narrative || 'No notes.').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
      </body></html>`;
      const w = window.open('', '_blank', 'width=700,height=500,resizable=yes,scrollbars=yes');
      if (w) { w.document.write(html); w.document.close(); }
    } catch { /* ignore */ }
  };

  const handleCallEditStart = () => {
    if (!callDetail) return;
    setCallEditForm({
      call_type_id: callDetail.call_type_id || 0,
      priority: callDetail.priority,
      location_address: callDetail.location_address,
      location_apt: callDetail.location_apt || '',
      location_city: callDetail.location_city || '',
      location_state: callDetail.location_state || '',
      cross_street: callDetail.cross_street || '',
      reporting_party: callDetail.reporting_party,
      reporting_phone: callDetail.reporting_phone || '',
      source: (callDetail as any).source || '',
      agency_id: callDetail.agency_id || null,
      status: callDetail.status,
    });
    setCallEditMode(true);
  };

  const handleCallEditSave = async () => {
    if (!callDetail) return;
    // If status is being changed to closed, prompt for disposition
    if (callEditForm.status === 'closed' && callDetail.status !== 'closed') {
      setCloseCallId(callDetail.id);
      setCloseCallCadNumber(callDetail.cad_number);
      setCloseDisposition('');
      setCloseCallPromptOpen(true);
      setCallEditMode(false);
      return;
    }
    try {
      const r = await api.patch(`/calls/${callDetail.id}`, callEditForm);
      setCallDetail(r.data);
      setCallEditMode(false);
      loadAll();
    } catch { /* ignore */ }
  };

  const handleCloseCallFromDetail = () => {
    if (!callDetail) return;
    setCloseCallId(callDetail.id);
    setCloseCallCadNumber(callDetail.cad_number);
    setCloseDisposition('');
    setCloseCallPromptOpen(true);
  };

  const handleAddNote = async () => {
    if (!callDetail || !newNote.trim()) return;
    try {
      const r = await api.post(`/calls/${callDetail.id}/notes`, { note: newNote.trim() });
      setCallDetail(r.data);
      setNewNote('');
      loadAll();
    } catch { /* ignore */ }
  };

  const handleRemovePerson = async (personnelId: number) => {
    if (!editUnitTarget) return;
    await api.delete(`/units/${editUnitTarget.id}/remove-personnel/${personnelId}`);
    const r = await api.get(`/units/${editUnitTarget.id}/personnel`);
    setUnitPersonnel(r.data);
  };

  const assignedIds = new Set(unitPersonnel.map((p) => p.personnel_id));
  const availablePersonnel = allPersonnel.filter((p) => !assignedIds.has(p.id));

  const geocodeSpecialAddress = async (addr: string): Promise<{ lat: number; lng: number } | null> => {
    // Mile marker: "I-44 at 69 MM", "I-44 MM 69", "I44 mile 69", "I-44 at MM 69", "I-44 69MM"
    const mmMatch = addr.match(/^(I[-\s]?\d+)\s+(?:at\s+)?(?:(?:MM|mile\s*(?:marker)?)\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s*(?:MM|mile\s*(?:marker)?))/i);
    if (mmMatch) {
      const intNum = mmMatch[1].replace(/[^0-9]/g, '');
      const mm = mmMatch[2] || mmMatch[3];
      try {
        const r = await api.get('/geocode/milemarker', { params: { interstate: intNum, mile: mm, center_lat: mapCenter.lat, center_lng: mapCenter.lng } });
        if (r.data.lat && r.data.lng) return { lat: r.data.lat, lng: r.data.lng };
      } catch { /* fall through */ }
      return null;
    }
    // Intersection: "Battlefield & Claremont", "Battlefield and Claremont",
    // "E. Battlefield Rd & US 65", "IS44 & Kansas Expy", etc.
    const intMatch = addr.match(/^([^&\/]+?)\s*(?:&|and|\/|\bat\b)\s*([^&\/]+)$/i);
    if (intMatch && !addr.match(/^\d+\s/)) {
      const st1 = intMatch[1].trim();
      const st2 = intMatch[2].trim();
      const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Core street name: drop leading directional + trailing street-type suffix
      // so "E. Battlefield Rd" matches "Battlefield" and "Kansas Expy" matches
      // OSM's "Kansas Expressway".
      const nameCore = (t: string): string => {
        let s = t.trim();
        s = s.replace(/^(?:NE|NW|SE|SW|[NSEW]|North|South|East|West)\.?\s+/i, '');
        s = s.replace(/\s+(?:Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pkwy|Parkway|Expy|Expwy|Expressway|Hwy|Highway|Way|Cir|Circle|Pl|Place|Ter|Terrace|Trl|Trail|Loop|Rte|Route|Rt)\.?$/i, '');
        return s.trim();
      };
      // Highway reference (matches OSM ref= tag): US 65, I-44, IS44, MO 13, Hwy 13
      const refFor = (t: string): string | null => {
        const m = t.match(/\b(interstate|ih|is|i|u\.?s\.?|us|state|sr|sh|mo|hwy|highway|route|rte|rt|fm)\b[\s.\-]*(\d+)/i)
          || t.match(/\b(i|is|ih)(\d+)\b/i);
        if (!m) return null;
        const pfx = m[1].toLowerCase().replace(/\./g, '');
        const num = m[2];
        if (['interstate', 'i', 'ih', 'is'].includes(pfx)) return `I[\\s-]?${num}`;
        if (['us', 'us'].includes(pfx)) return `US[\\s-]?${num}`;
        return `(?:MO|SR|SH|Route|Hwy|Highway|US)[\\s-]?${num}`;
      };
      const buildSet = (t: string, setName: string): string => {
        const core = nameCore(t) || t.trim();
        const stmts = [`way["highway"]["name"~"${escapeRe(core)}",i](${bbox});`];
        const ref = refFor(t);
        if (ref) stmts.push(`way["highway"]["ref"~"${ref}",i](${bbox});`);
        return `(${stmts.join('')})->.${setName};`;
      };
      const bbox = `${mapCenter.lat - 1},${mapCenter.lng - 1},${mapCenter.lat + 1},${mapCenter.lng + 1}`;
      // Use Overpass to find a shared node between the two streets (name OR ref)
      try {
        const q = `[out:json][timeout:25];${buildSet(st1, 'a')}${buildSet(st2, 'b')}node(w.a)(w.b);out 1;`;
        const ovR = await fetch('https://overpass-api.de/api/interpreter', {
          method: 'POST', body: `data=${encodeURIComponent(q)}`,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        const ovData = await ovR.json();
        if (ovData.elements?.length > 0) {
          return { lat: ovData.elements[0].lat, lng: ovData.elements[0].lon };
        }
      } catch { /* fall through */ }
      // Fallback to Nominatim — try a few phrasings (raw + normalized cores)
      const area = [callForm.location_city || jurisdictionCounty, callForm.location_state || jurisdictionState].filter(Boolean).join(', ');
      const variants = [
        `${st1} & ${st2}, ${area}`,
        `${st1} and ${st2}, ${area}`,
        `${nameCore(st1)} and ${nameCore(st2)}, ${area}`,
      ];
      for (const v of variants) {
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&q=${encodeURIComponent(v)}`);
          const d = await r.json();
          if (d.length > 0) return { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon) };
        } catch { /* try next */ }
      }
      return null;
    }
    return null;
  };

  const geocodeCoords = async (lat: number, lng: number) => {
    setPreviewCoords({ lat, lng });
    // Agency reverse geocode first (nearest imported address point) when policy permits.
    try {
      const rg = await api.post('/gis/geocode/reverse', { lat, lng });
      const rd = rg.data;
      if (rd.hit && rd.matched_address) {
        setPreviewAreaInfo(`${rd.matched_address} · Agency address point`);
        return;
      }
      if (!rd.allow_osm_fallback) {
        setPreviewAreaInfo('No agency address near this point');
        return;
      }
    } catch { /* fall through to OSM reverse geocode */ }
    try {
      const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
      const revData = await revR.json();
      const addr = revData?.address || {};
      const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
      setPreviewAreaInfo(parts.join(', '));
      const mainRoad = (addr.road || '').toLowerCase();
      const radius = 100;
      const query = `[out:json][timeout:5];way["highway"]["name"](around:${radius},${lat},${lng});out tags;`;
      const ovR = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const ovData = await ovR.json();
      const allNames: string[] = (ovData.elements || [])
        .map((e: any) => e.tags?.name)
        .filter((n: string | undefined) => n && n.toLowerCase() !== mainRoad);
      const streets = allNames.filter((n: string, i: number) => allNames.indexOf(n) === i);
      if (streets.length > 0) {
        setCallForm((f) => ({ ...f, cross_street: f.cross_street || streets.slice(0, 2).join(' / ') }));
      }
    } catch { /* ignore */ }
  };

  const nominatimSearch = async (query: string, vb?: string): Promise<any[]> => {
    const params = new URLSearchParams({ format: 'json', addressdetails: '1', q: query, limit: '5' });
    if (vb) { params.set('viewbox', vb); params.set('bounded', '0'); }
    const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
    return r.json();
  };

  // Geocode a "City, State" to its center so we can bias address searches to a
  // user-entered (mutual-aid / adjoining) city instead of the jurisdiction center.
  const geocodeCityCenter = async (city: string, state: string): Promise<{ lat: number; lng: number } | null> => {
    const q = [city, state].filter(Boolean).join(', ');
    if (!q) return null;
    try {
      const d = await nominatimSearch(q);
      if (d.length > 0) return { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon) };
    } catch { /* ignore */ }
    return null;
  };

  // Lettered/numbered state highways (e.g. MO supplemental "Route K", "US 65")
  // are tagged in OSM by `ref`, not by a "Highway K" name, so Nominatim often
  // misses them or matches a same-named road in the wrong county. When the
  // address references such a route, find the nearest matching way to the
  // (city-biased) center via Overpass and drop the pin there.
  const geocodeHighwayNearCity = async (
    addr: string,
    center: { lat: number; lng: number },
  ): Promise<{ lat: number; lng: number } | null> => {
    let ref: string | null = null;
    const letter = addr.match(/\b(?:state\s+(?:highway|route|hwy)|highway|hwy|route|rte|rt)\.?\s+([A-Za-z]{1,2})\b/i)
      || addr.match(/\b([A-Za-z]{1,2})\s+(?:highway|hwy)\b/i)
      || addr.match(/\bMO\s+([A-Za-z]{1,2})\b/i);
    if (letter) {
      ref = `^(MO )?${letter[1].toUpperCase()}$`;
    } else {
      const num = addr.match(/\b(?:U\.?S\.?|MO|SR|SH|state\s+(?:highway|route)|highway|hwy|route|rte|rt|interstate|i)\b[\s.\-]*(\d+)\b/i);
      if (num) ref = `[\\s-]${num[1]}$`;
    }
    if (!ref) return null;
    const q = `[out:json][timeout:25];way["highway"]["ref"~"${ref}"](around:30000,${center.lat},${center.lng});out center 40;`;
    try {
      const r = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST', body: `data=${encodeURIComponent(q)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const d = await r.json();
      const els = (d.elements || []).filter((e: any) => e.center);
      if (!els.length) return null;
      let best = els[0];
      let bd = Math.hypot(best.center.lat - center.lat, best.center.lon - center.lng);
      for (const e of els.slice(1)) {
        const dd = Math.hypot(e.center.lat - center.lat, e.center.lon - center.lng);
        if (dd < bd) { best = e; bd = dd; }
      }
      return { lat: best.center.lat, lng: best.center.lon };
    } catch { return null; }
  };

  const pickBestResult = (results: any[], centerLat: number, centerLng: number) => {
    if (results.length === 0) return null;
    if (results.length === 1) return results[0];
    // Pick the result closest to jurisdiction center
    let best = results[0];
    let bestDist = Math.hypot(parseFloat(best.lat) - centerLat, parseFloat(best.lon) - centerLng);
    for (let i = 1; i < results.length; i++) {
      const d = Math.hypot(parseFloat(results[i].lat) - centerLat, parseFloat(results[i].lon) - centerLng);
      if (d < bestDist) { best = results[i]; bestDist = d; }
    }
    return best;
  };

  const handlePreviewAddress = async () => {
    if (!callForm.location_address) return;
    // If we already have coordinates, use them
    if (callForm.location_lat && callForm.location_lng) {
      setPreviewCoords({ lat: callForm.location_lat, lng: callForm.location_lng });
      try {
        const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${callForm.location_lat}&lon=${callForm.location_lng}&zoom=17&addressdetails=1`);
        const revData = await revR.json();
        const addr = revData?.address || {};
        const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
        setPreviewAreaInfo(parts.join(', '));
      } catch { /* ignore */ }
      return;
    }
    setPreviewLoading(true);
    // Shared server-side geocoder: uses agency address points / centerlines when
    // the tenant's geocoding policy is agency_only / agency_then_osm. On a hit we
    // use it outright (exact address wins). For agency_only with no match we stop
    // rather than falling back to OSM; agency_then_osm / osm fall through below.
    try {
      const g = await api.post('/gis/geocode', {
        address: callForm.location_address,
        city: callForm.location_city || '',
        state: callForm.location_state || '',
      });
      const gd = g.data;
      if (gd.hit && gd.lat != null && gd.lng != null) {
        setCallForm((f) => ({ ...f, location_lat: gd.lat, location_lng: gd.lng }));
        setPreviewCoords({ lat: gd.lat, lng: gd.lng });
        setPreviewAreaInfo(
          [gd.matched_address, gd.source === 'agency-centerline' ? 'Agency centerline (approx)' : 'Agency address point']
            .filter(Boolean).join(' · '),
        );
        setPreviewLoading(false);
        return;
      }
      if (!gd.allow_osm_fallback) {
        setPreviewCoords({ lat: mapCenter.lat, lng: mapCenter.lng });
        setPreviewAreaInfo('No agency address match — drag pin to set location');
        setPreviewLoading(false);
        return;
      }
    } catch { /* fall through to OSM geocoding */ }
    // Try special address formats (mile markers, intersections)
    try {
      const specialResult = await geocodeSpecialAddress(callForm.location_address);
      if (specialResult) {
        setCallForm((f) => ({ ...f, location_lat: specialResult.lat, location_lng: specialResult.lng }));
        setPreviewCoords(specialResult);
        try {
          const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${specialResult.lat}&lon=${specialResult.lng}&zoom=17&addressdetails=1`);
          const revData = await revR.json();
          const addr = revData?.address || {};
          const areaParts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
          setPreviewAreaInfo(areaParts.join(', '));
          if (!callForm.cross_street && addr.road) {
            setCallForm((f) => ({ ...f, cross_street: addr.road }));
          }
        } catch { /* ignore */ }
        setPreviewLoading(false);
        return;
      }
    } catch { /* fall through to standard geocode */ }
    let result: any = null;
    // Prefer the call's City/State (mutual-aid / adjoining city); fall back to jurisdiction.
    const areaState = callForm.location_state || jurisdictionState;
    const areaLocality = callForm.location_city || jurisdictionCounty;
    // If the dispatcher entered a City/State that differs from the tenant default,
    // re-center the geocode bias on that city. Otherwise a common road name (e.g.
    // "Highway K", which exists in many MO counties) snaps back to the jurisdiction
    // center instead of the entered city (e.g. Richland vs Pleasant Hope).
    let biasCenter = mapCenter;
    const norm = (s: string) => s.trim().toLowerCase();
    const overrodeCity = !!callForm.location_city && norm(callForm.location_city) !== norm(defaultCity);
    const overrodeState = !!callForm.location_state && norm(callForm.location_state) !== norm(defaultState);
    if (overrodeCity || overrodeState) {
      const c = await geocodeCityCenter(callForm.location_city, areaState);
      if (c) biasCenter = c;
    }
    // Multi-strategy geocoding with viewbox bias centered on the (possibly overridden) city
    const vb = `${biasCenter.lng - 1},${biasCenter.lat - 1},${biasCenter.lng + 1},${biasCenter.lat + 1}`;
    try {
      // Strategy 1: Full address + city/county + state with viewbox
      const q1 = [callForm.location_address, areaLocality, areaState].filter(Boolean).join(', ');
      const d1 = await nominatimSearch(q1, vb);
      result = pickBestResult(d1, biasCenter.lat, biasCenter.lng);
      // Strategy 2: Address + state only (county can confuse Nominatim)
      if (!result && areaState) {
        const q2 = `${callForm.location_address}, ${areaState}`;
        const d2 = await nominatimSearch(q2, vb);
        result = pickBestResult(d2, biasCenter.lat, biasCenter.lng);
      }
      // Strategy 3: Just the address with viewbox bias
      if (!result) {
        const d3 = await nominatimSearch(callForm.location_address, vb);
        result = pickBestResult(d3, biasCenter.lat, biasCenter.lng);
      }
      // Strategy 4: Try without house number (street name only)
      if (!result) {
        const noNum = callForm.location_address.replace(/^\d+\s+/, '');
        if (noNum !== callForm.location_address) {
          const q4 = [noNum, areaLocality, areaState].filter(Boolean).join(', ');
          const d4 = await nominatimSearch(q4, vb);
          result = pickBestResult(d4, biasCenter.lat, biasCenter.lng);
        }
      }
    } catch { /* ignore */ }
    // Highway-route fallback: if the address names a lettered/numbered route and
    // the standard geocode either missed or landed far from the entered city,
    // snap to the nearest matching route (by OSM ref) around the biased center.
    const stdFar = !result || Math.hypot(
      parseFloat(result.lat) - biasCenter.lat, parseFloat(result.lon) - biasCenter.lng,
    ) > 0.4;
    if (stdFar) {
      const hw = await geocodeHighwayNearCity(callForm.location_address, biasCenter);
      if (hw) result = { lat: String(hw.lat), lon: String(hw.lng) };
    }
    if (result) {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      setCallForm((f) => ({ ...f, location_lat: lat, location_lng: lng }));
      setPreviewCoords({ lat, lng });
      const addr = result.address || {};
      const areaParts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
      setPreviewAreaInfo(areaParts.join(', '));
      // Fetch nearby streets for cross street
      if (!callForm.cross_street) {
        try {
          const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
          const revData = await revR.json();
          const mainRoad = (revData?.address?.road || '').toLowerCase();
          const radius = 100;
          const query = `[out:json][timeout:5];way["highway"]["name"](around:${radius},${lat},${lng});out tags;`;
          const ovR = await fetch('https://overpass-api.de/api/interpreter', {
            method: 'POST',
            body: `data=${encodeURIComponent(query)}`,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          });
          const ovData = await ovR.json();
          const allNames: string[] = (ovData.elements || [])
            .map((e: any) => e.tags?.name)
            .filter((n: string | undefined) => n && n.toLowerCase() !== mainRoad);
          const streets = allNames.filter((n: string, i: number) => allNames.indexOf(n) === i);
          if (streets.length > 0) {
            setCallForm((f) => ({ ...f, cross_street: streets.slice(0, 2).join(' / ') }));
          }
        } catch { /* cross street lookup failed, not critical */ }
      }
    } else {
      // No geocode result — center on the entered city (if resolved) else jurisdiction
      setPreviewCoords({ lat: biasCenter.lat, lng: biasCenter.lng });
      setPreviewAreaInfo('Address not found — drag pin to set location');
    }
    setPreviewLoading(false);
  };

  // Initialize/update preview map when coords change
  useEffect(() => {
    if (!previewCoords || !previewMapRef.current) return;
    if (!previewMapInstance.current) {
      const map = L.map(previewMapRef.current, { zoomControl: true, attributionControl: false })
        .setView([previewCoords.lat, previewCoords.lng], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
      previewMapInstance.current = map;
      const pinIcon = L.divIcon({
        className: '',
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        html: '<div style="width:24px;height:24px;background:#2196f3;border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>',
      });
      const marker = L.marker([previewCoords.lat, previewCoords.lng], { icon: pinIcon, draggable: true })
        .addTo(map);
      marker.on('dragend', async () => {
        const pos = marker.getLatLng();
        const lat = Math.round(pos.lat * 1e7) / 1e7;
        const lng = Math.round(pos.lng * 1e7) / 1e7;
        setCallForm((f) => ({ ...f, location_lat: lat, location_lng: lng }));
        try {
          const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
          const revData = await revR.json();
          const addr = revData?.address || {};
          const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
          setPreviewAreaInfo(parts.join(', '));
        } catch { /* ignore */ }
      });
      previewMarkerRef.current = marker;
    } else {
      previewMapInstance.current.setView([previewCoords.lat, previewCoords.lng], 15);
      previewMarkerRef.current?.setLatLng([previewCoords.lat, previewCoords.lng]);
    }
  }, [previewCoords]);

  const resetPreviewMap = () => {
    setPreviewCoords(null);
    setPreviewAreaInfo('');
    if (previewMapInstance.current) {
      previewMapInstance.current.remove();
      previewMapInstance.current = null;
      previewMarkerRef.current = null;
    }
  };

  const resetNewCallForm = () => {
    setCallForm({
      call_type_id: 0, priority: 3, location_address: '', location_lat: null,
      location_lng: null, location_apt: '', location_city: defaultCity, location_state: defaultState, cross_street: '',
      reporting_party: '', reporting_phone: '', source: '', location_source: '', narrative: '', agency_id: null,
      location_id: null,
    });
    setSelectedLocation(null);
    setLocationResults([]);
    resetPreviewMap();
  };

  const noteInputRef = React.useRef<HTMLInputElement>(null);

  const handleScheduleCall = async () => {
    if (!scheduleDate || !scheduleTime) return;
    try {
      const scheduledDatetime = `${scheduleDate}T${scheduleTime}:00`;
      await api.post('/scheduled-calls', {
        ...callForm,
        scheduled_datetime: scheduledDatetime,
        advance_minutes: scheduleAdvance,
      });
      setScheduleOpen(false);
      setNewCallOpen(false);
      resetNewCallForm();
      setScheduleDate('');
      setScheduleTime('');
      setScheduleAdvance(30);
    } catch { /* ignore */ }
  };

  const handleCreateCall = async () => {
    try {
      const created = await api.post('/calls', callForm);
      setCaseEntryOpen(false);
      setNewCallOpen(false);
      resetNewCallForm();
      loadAll();
      // Open the newly created call detail with case entry ready
      try {
        const r = await api.get(`/calls/${created.data.id}`);
        setCallDetail(r.data);
        setNewNote('');
        setCallEditMode(false);
        setCallDetailCaseEntryOpen(false);
        setCallDetailOpen(true);
        setTimeout(() => noteInputRef.current?.focus(), 200);
        if (r.data.location_address) {
          api.get('/calls/location-history', { params: { address: r.data.location_address, exclude_id: created.data.id, limit: 10 } })
            .then((hr) => setLocationHistory(hr.data))
            .catch(() => setLocationHistory([]));
        } else {
          setLocationHistory([]);
        }
      } catch { /* ignore */ }
    } catch { /* ignore */ }
  };

  const activeAlerts = statusCheckAlerts;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)', gap: 1 }}>
      {/* Status Check Reminder Alerts */}
      {activeAlerts.map((alert) => (
        <Alert
          key={alert.callId}
          severity="warning"
          icon={<Warning />}
          onClose={() => dismissAlert(alert.callId)}
          sx={{
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 },
            },
          }}
        >
          <strong>STATUS CHECK:</strong> {alert.cadNumber} ({alert.callType}) — Units on scene for {alert.minutesOverdue} min: <strong>{alert.units.join(', ')}</strong>
        </Alert>
      ))}

      {/* Upper table: Unit Status */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#1a1a2e', color: '#fff' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Unit Status</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained" size="small" startIcon={<Login />}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, textTransform: 'none', fontWeight: 700, fontSize: 12 }}
              onClick={(e) => setLogonAnchor(e.currentTarget)}
            >
              Log On Unit
            </Button>
            <Tooltip title="Refresh">
              <IconButton size="small" sx={{ color: '#fff' }} onClick={loadAll}><Refresh /></IconButton>
            </Tooltip>
          </Box>
        </Box>
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={DENSE_HEADER}>
                  <TableSortLabel active={unitSortBy === 'callsign'} direction={unitSortBy === 'callsign' ? unitSortDir : 'asc'} onClick={() => handleUnitSort('callsign')}>Unit</TableSortLabel>
                </TableCell>
                <TableCell sx={DENSE_HEADER}>Status</TableCell>
                <TableCell sx={DENSE_HEADER}>
                  <TableSortLabel active={unitSortBy === 'unit_type'} direction={unitSortBy === 'unit_type' ? unitSortDir : 'asc'} onClick={() => handleUnitSort('unit_type')}>Type</TableSortLabel>
                </TableCell>
                <TableCell sx={DENSE_HEADER}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <TableSortLabel active={unitSortBy === 'agency_name'} direction={unitSortBy === 'agency_name' ? unitSortDir : 'asc'} onClick={() => handleUnitSort('agency_name')}>Agency</TableSortLabel>
                    <Tooltip title="Filter agencies">
                      <IconButton size="small" sx={{ p: 0.25, color: unitAgencyFilter.length ? 'primary.main' : 'inherit' }} onClick={(e) => setAgencyFilterAnchor(e.currentTarget)}>
                        <FilterList sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={DENSE_HEADER}>Current Location</TableCell>
                <TableCell sx={DENSE_HEADER}>Assigned Call</TableCell>
                <TableCell sx={DENSE_HEADER}>Call Location</TableCell>
                <TableCell sx={DENSE_HEADER}>Updated</TableCell>
                <TableCell sx={DENSE_CELL} width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {units.filter((u) => u.is_logged_on !== false)
                .filter((u) => unitAgencyFilter.length === 0 || unitAgencyFilter.includes(u.agency_name))
                .sort((a, b) => {
                  const aVal = (a[unitSortBy] || '').toString().toLowerCase();
                  const bVal = (b[unitSortBy] || '').toString().toLowerCase();
                  return unitSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                })
                .map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell
                    sx={{ ...DENSE_CELL, fontWeight: 700, fontFamily: 'monospace', bgcolor: u.status_color, color: '#fff', cursor: 'pointer' }}
                    onClick={(e) => handleStatusClick(e, u)}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {u.unit_type_icon && <img src={u.unit_type_icon} alt="" style={{ width: 18, height: 18, objectFit: 'contain', verticalAlign: 'middle' }} />}
                      {u.callsign}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, fontWeight: 600, fontSize: 11 }}>
                    <Chip size="small" label={u.status_name || '—'} sx={{ bgcolor: u.status_color, color: '#fff', height: 20, fontSize: 11, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{u.unit_type}</TableCell>
                  <TableCell sx={DENSE_CELL}>{u.agency_name}</TableCell>
                  <TableCell sx={DENSE_CELL}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {u.location_updated_at && (
                        <Box component="span" sx={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                          bgcolor: (() => {
                            const age = (Date.now() - new Date(u.location_updated_at + (u.location_updated_at.endsWith('Z') ? '' : 'Z')).getTime()) / 60000;
                            return age <= 3 ? '#4caf50' : age <= 6 ? '#ff9800' : '#f44336';
                          })(),
                        }} />
                      )}
                      {u.current_location || '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, fontFamily: 'monospace' }}>
                    {u.assigned_call_cad_number || '—'}
                    {u.transport_destination && <Box component="span" sx={{ ml: 0.5, fontSize: 11, color: '#e91e63' }}>→ {u.transport_destination}</Box>}
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, color: '#ccc', fontSize: 11 }}>
                    {u.assigned_call_id ? (calls.find((c) => c.id === u.assigned_call_id)?.location_address || '—') : '—'}
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, color: 'text.secondary' }}>
                    {u.updated_at ? `${formatDateShort(u.updated_at)} ${formatTime24(u.updated_at)}` : '—'}
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, p: 0 }}>
                    <IconButton size="small" onClick={(e) => handleStatusClick(e, u)}>
                      <ArrowDropDown fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {units.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>No units configured</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Status change menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>Change Status</Typography>
        <Divider />
        {statusTypes.map((st) => (
          <MenuItem key={st.id} onClick={() => handleStatusChange(st.id)}>
            <Chip size="small" label={st.code} sx={{ bgcolor: st.color, color: '#fff', mr: 1, width: 40 }} />
            {st.name}
          </MenuItem>
        ))}
        <Divider />
        {selectedUnit && selectedUnit.assigned_call_id && (
          <MenuItem onClick={() => { handleClearUnit(selectedUnit.id); setAnchorEl(null); setSelectedUnit(null); }} sx={{ color: '#ff9800', fontWeight: 700 }}>
            <Logout fontSize="small" sx={{ mr: 1 }} /> Clear from Call
          </MenuItem>
        )}
        {selectedUnit && (
          <MenuItem onClick={() => handleEditUnit(selectedUnit)}>
            <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Unit
          </MenuItem>
        )}
        {selectedUnit && (
          <MenuItem onClick={() => { handleLogoff(selectedUnit.id); setAnchorEl(null); }} sx={{ color: '#f44336' }}>
            <Logout fontSize="small" sx={{ mr: 1 }} /> Log Off
          </MenuItem>
        )}
      </Menu>

      {/* Transport Destination Dialog */}
      <Dialog open={transportDialogOpen} onClose={() => setTransportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transport Destination — {pendingTransportUnit?.callsign}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Select the transport destination for this unit.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Destination</InputLabel>
            <Select
              value={transportDest}
              label="Destination"
              onChange={(e) => { setTransportDest(e.target.value); if (e.target.value !== '__OTHER__') setTransportOther(''); }}
              autoFocus
            >
              {destinations.map((d) => (
                <MenuItem key={d.id} value={d.name}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{d.name}</Typography>
                    {d.address && <Typography variant="caption" color="text.secondary">{d.address}</Typography>}
                  </Box>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem value="__OTHER__"><em>Other (enter manually)</em></MenuItem>
            </Select>
          </FormControl>
          {transportDest === '__OTHER__' && (
            <TextField
              fullWidth
              label="Enter destination"
              value={transportOther}
              onChange={(e) => setTransportOther(e.target.value)}
              placeholder="e.g., Patient's residence, urgent care clinic"
              autoFocus
            />
          )}
          {transportDest && transportDest !== '__OTHER__' && (() => {
            const sel = destinations.find((d) => d.name === transportDest);
            if (!sel) return null;
            return (
              <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                {sel.address && <Typography variant="body2"><strong>Address:</strong> {sel.address}</Typography>}
                {sel.phone && <Typography variant="body2"><strong>Phone:</strong> {sel.phone}</Typography>}
                {sel.notes && <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>{sel.notes}</Typography>}
              </Paper>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransportConfirm}
            disabled={!transportDest || (transportDest === '__OTHER__' && !transportOther.trim())}
          >Set Transporting</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Unit (Personnel) Dialog */}
      <Dialog open={editUnitOpen} onClose={() => setEditUnitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Unit — {editUnitTarget?.callsign}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 700 }}>Assigned Personnel</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={availablePersonnel}
              value={selectedPerson}
              onChange={(_, v) => setSelectedPerson(v)}
              getOptionLabel={(o) => `${o.dsn} — ${o.last_name}, ${o.first_name}`}
              renderInput={(params) => <TextField {...params} label="Add Personnel" size="small" />}
            />
            <Button variant="contained" size="small" disabled={!selectedPerson} onClick={handleAssignPerson}>
              Assign
            </Button>
          </Box>
          {unitPersonnel.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No personnel assigned</Typography>
          ) : (
            <List dense>
              {unitPersonnel.map((p) => (
                <ListItem key={p.personnel_id}>
                  <ListItemText
                    primary={`${p.dsn} — ${p.last_name}, ${p.first_name}`}
                    secondary={p.role}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => handleRemovePerson(p.personnel_id)} sx={{ color: '#f44336' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUnitOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Log On Unit menu */}
      <Menu anchorEl={logonAnchor} open={Boolean(logonAnchor)} onClose={() => setLogonAnchor(null)}>
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>Select Unit to Log On</Typography>
        <Divider />
        {units.filter((u) => !u.is_logged_on).length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">All units are logged on</Typography>
          </MenuItem>
        )}
        {units.filter((u) => !u.is_logged_on).map((u) => (
          <MenuItem key={u.id} onClick={() => { handleLogon(u.id); setLogonAnchor(null); }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, mr: 1 }}>{u.callsign}</Typography>
            <Typography variant="caption" color="text.secondary">{u.agency_name}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Agency filter popover for the Unit Status grid */}
      <Popover
        open={Boolean(agencyFilterAnchor)}
        anchorEl={agencyFilterAnchor}
        onClose={() => setAgencyFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 200 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Show Agencies</Typography>
            <Link component="button" variant="caption" underline="hover" onClick={() => setUnitAgencyFilter([])}>All</Link>
          </Box>
          <Divider sx={{ mb: 0.5 }} />
          {(() => {
            const names = Array.from(new Set(units.filter((u) => u.is_logged_on !== false).map((u) => u.agency_name).filter(Boolean))).sort();
            if (names.length === 0) return <Typography variant="body2" color="text.secondary">No units logged on</Typography>;
            return (
              <FormGroup>
                {names.map((name) => {
                  const checked = unitAgencyFilter.length === 0 || unitAgencyFilter.includes(name);
                  return (
                    <FormControlLabel
                      key={name}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={(e) => {
                            const base = unitAgencyFilter.length === 0 ? names : unitAgencyFilter;
                            let next = e.target.checked ? Array.from(new Set([...base, name])) : base.filter((n) => n !== name);
                            // If everything is selected, revert to empty (= all) so header icon isn't highlighted.
                            if (next.length === names.length) next = [];
                            setUnitAgencyFilter(next);
                          }}
                        />
                      }
                      label={<Typography variant="body2">{name}</Typography>}
                    />
                  );
                })}
              </FormGroup>
            );
          })()}
        </Box>
      </Popover>

      {/* Lower table: Pending Calls */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#1a1a2e', color: '#fff' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Calls for Service</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setNewCallOpen(true)}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, fontWeight: 700 }}
            >
              New Call
            </Button>
            <Chip label={`${calls.length} active`} size="small" color="warning" />
          </Box>
        </Box>
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={DENSE_HEADER}>CAD #</TableCell>
                <TableCell sx={DENSE_HEADER}>Case #</TableCell>
                <TableCell sx={DENSE_HEADER}>Priority</TableCell>
                <TableCell sx={DENSE_HEADER}>Type</TableCell>
                <TableCell sx={DENSE_HEADER}>Location</TableCell>
                <TableCell sx={DENSE_HEADER}>Status</TableCell>
                <TableCell sx={DENSE_HEADER}>Reporting Party</TableCell>
                <TableCell sx={DENSE_HEADER}>Agency</TableCell>
                <TableCell sx={DENSE_HEADER}>Created</TableCell>
                <TableCell sx={DENSE_HEADER}>Units</TableCell>
                <TableCell sx={DENSE_CELL} width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((c) => {
                const assignedUnits = units.filter((u) => u.assigned_call_id === c.id);
                const isPendingAlert = pendingCallIds.has(c.id);
                return (
                <TableRow key={c.id} hover sx={{
                  bgcolor: c.priority === 1 ? 'rgba(244,67,54,0.08)' : undefined,
                  ...(isPendingAlert && { animation: 'pendingBlink 1s ease-in-out infinite', '@keyframes pendingBlink': { '0%, 100%': { bgcolor: 'transparent' }, '50%': { bgcolor: 'rgba(255,152,0,0.2)' } } }),
                }}>
                  <TableCell sx={{ ...DENSE_CELL, fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => handleCallClick(c.id)}>{c.cad_number}</TableCell>
                  <TableCell sx={{ ...DENSE_CELL, fontFamily: 'monospace' }}>{c.case_number || '—'}</TableCell>
                  <TableCell sx={DENSE_CELL}>
                    <Chip size="small" label={`P${c.priority}`} sx={{ bgcolor: PRIORITY_COLORS[c.priority], color: '#fff', fontWeight: 700, height: 20, fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{c.call_type_code} - {c.call_type_name}</TableCell>
                  <TableCell sx={DENSE_CELL}>{c.location_address}</TableCell>
                  <TableCell sx={DENSE_CELL}><Chip size="small" label={c.status} sx={{ height: 20, fontSize: 11 }} /></TableCell>
                  <TableCell sx={DENSE_CELL}>{c.reporting_party}</TableCell>
                  <TableCell sx={DENSE_CELL}>{c.agency_name}</TableCell>
                  <TableCell sx={{ ...DENSE_CELL, color: 'text.secondary' }}>
                    {c.created_at ? `${formatDateShort(c.created_at)} ${formatTime24(c.created_at)}` : '—'}
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>
                    {assignedUnits.map((u) => (
                      <Chip key={u.id} size="small" label={u.callsign} sx={{ height: 20, fontSize: 11, mr: 0.5, bgcolor: u.status_color, color: '#fff' }}
                        onDelete={() => handleClearUnit(u.id)} />
                    ))}
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, p: 0 }}>
                    <IconButton size="small" onClick={() => handleDispatchClick(c.id)} title="Dispatch Unit">
                      <Add fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {calls.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center" sx={{ color: 'text.secondary', py: 4 }}>No pending calls</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* New Call Dialog */}
      <Dialog open={newCallOpen} onClose={() => { setNewCallOpen(false); resetNewCallForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>New Call for Service</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Autocomplete
                freeSolo
                options={locationResults}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : `@${opt.name} — ${opt.address}`}
                inputValue={callForm.location_address}
                onInputChange={(_e, value, reason) => {
                  // Typing a manual address detaches any previously-picked premise.
                  setCallForm({ ...callForm, location_address: value, ...(reason === 'input' ? { location_id: null } : {}) });
                  if (reason === 'input') setSelectedLocation(null);
                  if (value.startsWith('@') && value.length > 1) {
                    if (locationTimer.current) clearTimeout(locationTimer.current);
                    locationTimer.current = setTimeout(() => {
                      api.get(`/locations/search?q=${encodeURIComponent(value.slice(1))}`)
                        .then((r) => setLocationResults(r.data)).catch(() => {});
                    }, 300);
                  } else { setLocationResults([]); }
                }}
                onChange={(_e, val) => {
                  if (val && typeof val !== 'string') {
                    const fullAddr = [val.address, val.city, val.state, val.zip_code].filter(Boolean).join(', ');
                    let narrative = callForm.narrative;
                    if (val.hazard_info) narrative = `*** HAZARD: ${val.hazard_info} ***\n${narrative}`;
                    if (val.additional_notes) narrative = `${narrative}\nLocation Notes: ${val.additional_notes}`;
                    setCallForm({
                      ...callForm, location_address: fullAddr,
                      location_apt: val.apt_suite || callForm.location_apt,
                      location_lat: val.latitude, location_lng: val.longitude,
                      location_id: val.id,
                      narrative: narrative.trim(),
                    });
                    setSelectedLocation(val);
                    if (val.latitude && val.longitude) geocodeCoords(val.latitude, val.longitude);
                  }
                }}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>@{opt.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{opt.address}{opt.city ? `, ${opt.city}` : ''}</Typography>
                      {opt.hazard_info && <Chip size="small" icon={<Warning />} label="Hazard" color="warning" sx={{ ml: 1 }} />}
                    </Box>
                  </li>
                )}
                onBlur={() => { if (callForm.location_address && !callForm.location_address.startsWith('@')) handlePreviewAddress(); }}
                renderInput={(params) => (
                  <TextField {...params} autoFocus fullWidth inputRef={locationInputRef} label="Location / Address (type @ to search saved locations)" />
                )}
              />
              {callForm.location_source && (
                <Chip
                  size="small"
                  color={callForm.location_source === 'billing' ? 'warning' : 'info'}
                  label={`Location from ${LOCATION_SOURCE_LABELS[callForm.location_source] || callForm.location_source} — verify with caller`}
                  sx={{ mt: 1 }}
                />
              )}
              {selectedLocation?.hazard_info && (
                <Paper sx={{ mt: 1, p: 1, bgcolor: '#4a2800', border: '1px solid #ff9800' }}>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                    <Warning sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    HAZARD: {selectedLocation.hazard_info}
                  </Typography>
                </Paper>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Apt / Suite" value={callForm.location_apt} onChange={(e) => setCallForm({ ...callForm, location_apt: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField fullWidth label="City" value={callForm.location_city} onChange={(e) => setCallForm({ ...callForm, location_city: e.target.value })} helperText="Defaults to your jurisdiction; override for mutual aid / adjoining city" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="State" value={callForm.location_state} onChange={(e) => setCallForm({ ...callForm, location_state: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Cross Street" value={callForm.cross_street} onChange={(e) => setCallForm({ ...callForm, cross_street: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Latitude" type="number" value={callForm.location_lat ?? ''} onChange={(e) => setCallForm({ ...callForm, location_lat: e.target.value ? Number(e.target.value) : null })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Longitude" type="number" value={callForm.location_lng ?? ''} onChange={(e) => setCallForm({ ...callForm, location_lng: e.target.value ? Number(e.target.value) : null })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                startIcon={<Place />}
                variant="outlined"
                size="small"
                disabled={!callForm.location_address || previewLoading}
                onClick={handlePreviewAddress}
              >
                {previewLoading ? 'Geocoding...' : 'Preview Address'}
              </Button>
              {previewCoords && (
                <Button
                  startIcon={<Clear />}
                  variant="outlined"
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                  onClick={() => { resetPreviewMap(); setCallForm((f) => ({ ...f, location_lat: null, location_lng: null })); }}
                >
                  Clear Address
                </Button>
              )}
            </Grid>
            {previewCoords && (
              <Grid size={{ xs: 12 }}>
                <div ref={previewMapRef} style={{ height: 200, width: '100%', borderRadius: 4, border: '1px solid #555' }} />
                <Typography variant="caption" sx={{ mt: 0.5, color: '#aaa', display: 'block' }}>
                  Drag pin to fine-tune location
                </Typography>
                {previewAreaInfo && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: '#90caf9', fontWeight: 600 }}>
                    📍 {previewAreaInfo}
                  </Typography>
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={callTypes}
                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                value={callTypes.find((ct) => ct.id === callForm.call_type_id) || null}
                onChange={(_, val) => {
                  setCallForm({ ...callForm, call_type_id: val?.id || 0, priority: val?.priority ?? callForm.priority });
                }}
                renderInput={(params) => <TextField {...params} label="Call Type" margin="normal" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={callPriorities}
                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                value={callPriorities.find((cp) => cp.code === callForm.priority) || null}
                onChange={(_, val) => setCallForm({ ...callForm, priority: val?.code ?? 3 })}
                renderInput={(params) => <TextField {...params} label="Priority" margin="normal" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={agencies}
                getOptionLabel={(opt) => opt.name}
                value={agencies.find((a) => a.id === callForm.agency_id) || null}
                onChange={(_, val) => setCallForm({ ...callForm, agency_id: val?.id || null })}
                renderInput={(params) => <TextField {...params} label="Agency" margin="normal" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Reporting Party" value={callForm.reporting_party} onChange={(e) => setCallForm({ ...callForm, reporting_party: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Phone" value={callForm.reporting_phone} onChange={(e) => setCallForm({ ...callForm, reporting_phone: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth select label="Source" value={callForm.source} onChange={(e) => setCallForm({ ...callForm, source: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="911">911</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Radio">Radio</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField fullWidth multiline rows={3} label="Narrative" value={callForm.narrative} onChange={(e) => setCallForm({ ...callForm, narrative: e.target.value })} sx={{ flexGrow: 1 }} />
                <Tooltip title="Case Entry — guided interview">
                  <Button
                    variant="outlined" size="small" sx={{ mt: 1, minWidth: 'auto', px: 1 }}
                    onClick={() => setCaseEntryOpen(true)}
                    startIcon={<QuestionAnswer />}
                  >
                    Case Entry
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
            {caseEntryOpen && (
              <Grid size={{ xs: 12 }}>
                <CaseEntryPanel
                  callTypeCode={callTypes.find(ct => ct.id === callForm.call_type_id)?.code}
                  onNarrativeUpdate={(text) => {
                    setCallForm((f: any) => ({ ...f, narrative: f.narrative ? f.narrative + '\n' + text : text }));
                  }}
                  onClose={() => setCaseEntryOpen(false)}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNewCallOpen(false); resetNewCallForm(); setCaseEntryOpen(false); }}>Cancel</Button>
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            onClick={() => setScheduleOpen(true)}
            sx={{ mr: 1 }}
          >
            Schedule Call
          </Button>
          <Button variant="contained" onClick={handleCreateCall}>Create Call</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Call Dialog */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule Call for Service</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set when this call should be scheduled and how far in advance it should be created in the pending queue.
          </Typography>
          <TextField
            fullWidth
            label="Scheduled Date"
            type="date"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Scheduled Time"
            type="time"
            value={scheduleTime}
            onChange={(e) => setScheduleTime(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Create call in pending queue this many minutes before scheduled time"
            type="number"
            value={scheduleAdvance}
            onChange={(e) => setScheduleAdvance(Number(e.target.value))}
            slotProps={{ input: { inputProps: { min: 0 } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleScheduleCall}
            disabled={!scheduleDate || !scheduleTime || !callForm.call_type_id}
          >
            Schedule Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call Detail / Notes Dialog */}
      <Dialog open={callDetailOpen} onClose={() => { setCallDetailOpen(false); setCallEditMode(false); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{callDetail?.cad_number} — {callDetail?.call_type_code} {callDetail?.call_type_name}</span>
          {!callEditMode && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {!callDetail?.case_number && (
                <Tooltip title="Assign Case Number">
                  <IconButton size="small" onClick={async () => {
                    try {
                      const r = await api.post(`/calls/${callDetail?.id}/assign-case-number`);
                      setCallDetail(r.data);
                      loadAll();
                    } catch (err: any) {
                      alert(err?.response?.data?.detail || 'Failed to assign case number');
                    }
                  }}>
                    <Gavel fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => { setMergeDialogOpen(true); setMergeTargetId(null); }} title="Merge Call">
                <MergeType fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCallEditStart} title="Edit Call">
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {callEditMode ? (
            <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField select fullWidth label="Call Type" value={String(callEditForm.call_type_id)}
                  onChange={(e) => setCallEditForm({ ...callEditForm, call_type_id: Number(e.target.value) })}>
                  {callTypes.map((ct) => <MenuItem key={ct.id} value={String(ct.id)}>{ct.code} - {ct.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField select fullWidth label="Priority" value={String(callEditForm.priority)}
                  onChange={(e) => setCallEditForm({ ...callEditForm, priority: Number(e.target.value) })}>
                  {callPriorities.length > 0
                    ? callPriorities.map((p) => <MenuItem key={p.id} value={String(p.code)}>P{p.code} - {p.name}</MenuItem>)
                    : [1,2,3,4,5].map((p) => <MenuItem key={p} value={String(p)}>P{p}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField select fullWidth label="Status" value={callEditForm.status}
                  onChange={(e) => setCallEditForm({ ...callEditForm, status: e.target.value })}>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="dispatched">Dispatched</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField fullWidth label="Location / Address" value={callEditForm.location_address}
                  onChange={(e) => setCallEditForm({ ...callEditForm, location_address: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth label="Apt / Suite" value={callEditForm.location_apt}
                  onChange={(e) => setCallEditForm({ ...callEditForm, location_apt: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField fullWidth label="City" value={callEditForm.location_city}
                  onChange={(e) => setCallEditForm({ ...callEditForm, location_city: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth label="State" value={callEditForm.location_state}
                  onChange={(e) => setCallEditForm({ ...callEditForm, location_state: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Cross Street" value={callEditForm.cross_street}
                  onChange={(e) => setCallEditForm({ ...callEditForm, cross_street: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField select fullWidth label="Agency" value={String(callEditForm.agency_id || '')}
                  onChange={(e) => setCallEditForm({ ...callEditForm, agency_id: e.target.value ? Number(e.target.value) : null })}>
                  <MenuItem value="">— None —</MenuItem>
                  {agencies.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Reporting Party" value={callEditForm.reporting_party}
                  onChange={(e) => setCallEditForm({ ...callEditForm, reporting_party: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Phone" value={callEditForm.reporting_phone}
                  onChange={(e) => setCallEditForm({ ...callEditForm, reporting_phone: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth select label="Source" value={callEditForm.source}
                  onChange={(e) => setCallEditForm({ ...callEditForm, source: e.target.value })}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="911">911</MenuItem>
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="Radio">Radio</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Priority</Typography>
                <Typography variant="body2">P{callDetail?.priority}</Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body2">{callDetail?.status}</Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Agency</Typography>
                <Typography variant="body2">{callDetail?.agency_name || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body2">{callDetail?.location_address}</Typography>
                {(callDetail?.location_city || callDetail?.location_state) && (
                  <Typography variant="body2" color="text.secondary">
                    {[callDetail?.location_city, callDetail?.location_state].filter(Boolean).join(', ')}
                  </Typography>
                )}
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Reporting Party</Typography>
                <Typography variant="body2">{callDetail?.reporting_party || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 2 }}>
                <Typography variant="caption" color="text.secondary">Source</Typography>
                <Typography variant="body2">{callDetail?.source || '—'}</Typography>
              </Grid>
            </Grid>
          )}
          {/* Premise Documents (from the saved location this call was created at) */}
          {callDetail?.premise_documents && callDetail.premise_documents.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Description fontSize="small" /> Premise Documents ({callDetail.premise_documents.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {callDetail.premise_documents.map((d) => (
                  <Button key={d.id} size="small" variant="outlined" startIcon={<OpenInNew />}
                    onClick={() => window.open(d.view_url, '_blank', 'noopener')}>
                    {(DOC_CATEGORY_LABELS[d.category] || 'Document')}{d.notes ? ` — ${d.notes}` : ''}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
          {/* Dispatch Units Section */}
          {callDetail && callDetail.status !== 'closed' && callDetail.status !== 'cancelled' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Dispatch Units</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#1a1a2e' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>Distance</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }} width={80} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const callLat = callDetail.location_lat;
                      const callLng = callDetail.location_lng;
                      const haversine = (lat1: number, lng1: number, lat2: number, lng2: number) => {
                        const R = 3958.8;
                        const dLat = (lat2 - lat1) * Math.PI / 180;
                        const dLng = (lng2 - lng1) * Math.PI / 180;
                        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
                        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                      };
                      const available = units
                        .filter((u) => u.is_logged_on !== false && u.assigned_call_id !== callDetail.id)
                        .map((u) => {
                          const hasLoc = u.location_lat && u.location_lng && callLat && callLng;
                          const dist = hasLoc ? haversine(u.location_lat!, u.location_lng!, callLat!, callLng!) : null;
                          const isAvailable = !u.assigned_call_id && (u.status_code === 'AVL' || u.status_name?.toLowerCase() === 'available');
                          const score = (isAvailable ? 0 : 1000) + (dist ?? 9999);
                          return { ...u, dist, isAvailable, score };
                        })
                        .sort((a, b) => a.score - b.score);
                      return available.map((u) => (
                        <TableRow key={u.id} hover sx={{ opacity: u.isAvailable ? 1 : 0.6 }}>
                          <TableCell sx={{ py: 0.3, fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              {u.unit_type_icon && <img src={u.unit_type_icon} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />}
                              {u.callsign}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.3, fontSize: 12 }}>{u.unit_type}</TableCell>
                          <TableCell sx={{ py: 0.3, fontSize: 12 }}>
                            <Chip label={u.status_name || '—'} size="small" sx={{ bgcolor: u.status_color, color: '#fff', height: 20, fontSize: 11 }} />
                          </TableCell>
                          <TableCell sx={{ py: 0.3, fontSize: 12 }}>
                            {u.dist !== null ? `${u.dist.toFixed(1)} mi` : '—'}
                          </TableCell>
                          <TableCell sx={{ py: 0.3 }}>
                            <Button
                              size="small" variant="contained" color="warning"
                              sx={{ fontSize: 10, py: 0, px: 1, minWidth: 0 }}
                              onClick={async () => {
                                try {
                                  await api.post(`/units/${u.id}/dispatch/${callDetail.id}`);
                                  const r = await api.get(`/calls/${callDetail.id}`);
                                  setCallDetail(r.data);
                                  loadAll();
                                } catch (err: any) {
                                  alert(err?.response?.data?.detail || 'Dispatch failed');
                                }
                              }}
                            >
                              Dispatch
                            </Button>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {/* Unit time punches — super-admin corrections */}
          {callDetail && isSuperadmin && (
            <CallTimesPanel
              callId={callDetail.id}
              isSuperadmin={isSuperadmin}
              onChanged={async () => {
                try {
                  const r = await api.get(`/calls/${callDetail.id}`);
                  setCallDetail(r.data);
                } catch { /* keep dialog open on refresh failure */ }
                loadAll();
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Narrative / Notes</Typography>
            {callDetail && callDetail.status !== 'closed' && callDetail.status !== 'cancelled' && !callDetailCaseEntryOpen && (
              <Tooltip title="Case Entry — guided interview">
                <Button size="small" variant="outlined" sx={{ fontSize: 11, py: 0, px: 1 }}
                  startIcon={<QuestionAnswer sx={{ fontSize: 14 }} />}
                  onClick={() => setCallDetailCaseEntryOpen(true)}>
                  Case Entry
                </Button>
              </Tooltip>
            )}
          </Box>
          {callDetailCaseEntryOpen && callDetail && (
            <Box sx={{ mb: 2 }}>
              <CaseEntryPanel
                callTypeCode={callTypes.find(ct => ct.id === callDetail.call_type_id)?.code}
                onNarrativeUpdate={(text) => {
                  // Add the case entry narrative as a note to the call
                  api.post(`/calls/${callDetail.id}/notes`, { note: text }).then((r) => {
                    setCallDetail(r.data);
                    loadAll();
                  }).catch(() => {});
                }}
                onClose={() => setCallDetailCaseEntryOpen(false)}
              />
            </Box>
          )}
          <Paper sx={{ p: 1.5, bgcolor: '#111', maxHeight: 250, overflow: 'auto', mb: 2, fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap' }}>
            {callDetail?.narrative || 'No notes yet.'}
          </Paper>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth size="small" placeholder="Add a note..."
              inputRef={noteInputRef}
              value={newNote} onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
            />
            <Button variant="contained" size="small" onClick={handleAddNote} disabled={!newNote.trim()}>
              Add
            </Button>
          </Box>
          {locationHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: '#ff9800' }}>Location History ({locationHistory.length})</Typography>
              <Paper sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#1a1a2e' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...DENSE_HEADER, color: '#aaa' }}>CAD #</TableCell>
                      <TableCell sx={{ ...DENSE_HEADER, color: '#aaa' }}>Date</TableCell>
                      <TableCell sx={{ ...DENSE_HEADER, color: '#aaa' }}>Call Type</TableCell>
                      <TableCell sx={{ ...DENSE_HEADER, color: '#aaa' }}>Status</TableCell>
                      <TableCell sx={{ ...DENSE_HEADER, color: '#aaa' }}>Disposition</TableCell>
                      <TableCell sx={{ ...DENSE_HEADER, color: '#aaa' }}>Reporting Party</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationHistory.map((h: any) => (
                      <TableRow key={h.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleHistoryCallClick(h.id)}>
                        <TableCell sx={{ ...DENSE_CELL, fontWeight: 700, fontFamily: 'monospace', color: '#2196f3', textDecoration: 'underline' }}>{h.cad_number}</TableCell>
                        <TableCell sx={{ ...DENSE_CELL, color: '#ccc' }}>{h.created_at ? new Date(h.created_at.endsWith('Z') ? h.created_at : h.created_at + 'Z').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '—'}</TableCell>
                        <TableCell sx={{ ...DENSE_CELL, color: '#ccc' }}>{h.call_type_code ? `${h.call_type_code} - ${h.call_type_name}` : '—'}</TableCell>
                        <TableCell sx={DENSE_CELL}><Chip size="small" label={h.status} sx={{ height: 18, fontSize: 10 }} /></TableCell>
                        <TableCell sx={{ ...DENSE_CELL, color: '#ccc' }}>{h.disposition || '—'}</TableCell>
                        <TableCell sx={{ ...DENSE_CELL, color: '#ccc' }}>{h.reporting_party || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {callEditMode ? (
            <>
              <Button onClick={() => setCallEditMode(false)}>Cancel Edit</Button>
              <Button variant="contained" onClick={handleCallEditSave}>Save Changes</Button>
            </>
          ) : (
            <>
              {callDetail && callDetail.status !== 'closed' && callDetail.status !== 'cancelled' && (
                <Button color="error" variant="outlined" onClick={handleCloseCallFromDetail}>Close Call</Button>
              )}
              <Button onClick={() => { setCallDetailOpen(false); setCallEditMode(false); setCallDetailCaseEntryOpen(false); }}>Done</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Merge Call Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge Call — {callDetail?.cad_number}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the <b>primary call</b> to merge this call into. All narrative, reporting party info,
            and assigned units will be transferred to the primary call. This call ({callDetail?.cad_number}) will be closed.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Merge into (Primary Call)</InputLabel>
            <Select
              value={String(mergeTargetId || '')}
              label="Merge into (Primary Call)"
              onChange={(e: SelectChangeEvent) => setMergeTargetId(Number(e.target.value) || null)}
            >
              {calls.filter((c) => c.id !== callDetail?.id && c.status !== 'closed' && c.status !== 'cancelled').map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.cad_number} — {c.call_type_code} {c.call_type_name} ({c.location_address})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleMergeCall} disabled={!mergeTargetId}>
            Merge & Close This Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispatch Unit Dialog */}
      <Dialog open={dispatchDialogOpen} onClose={() => setDispatchDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dispatch Unit to Call</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select a logged-on unit to dispatch:
          </Typography>
          <List dense>
            {units.filter((u) => u.is_logged_on && !u.assigned_call_id).map((u) => (
              <ListItem key={u.id} component="div" onClick={() => handleDispatchUnit(u.id)}
                sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: u.status_color, mr: 1.5 }} />
                <ListItemText primary={u.callsign} secondary={`${u.agency_name} — ${u.status_name}`} />
              </ListItem>
            ))}
            {units.filter((u) => u.is_logged_on && !u.assigned_call_id).length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                No available units
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Close Call Prompt Dialog */}
      <Dialog open={closeCallPromptOpen} onClose={handleLeavePending} maxWidth="xs" fullWidth>
        <DialogTitle>All Units Cleared — {closeCallCadNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            All units have been cleared from this call. Would you like to close it or leave it pending?
          </Typography>
          <TextField select fullWidth label="Disposition" value={closeDisposition}
            onChange={(e) => setCloseDisposition(e.target.value)}>
            <MenuItem value="">— Select Disposition —</MenuItem>
            {dispositions.map((d) => <MenuItem key={d.id} value={d.code}>{d.code} - {d.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLeavePending}>Leave Pending</Button>
          <Button variant="contained" color="error" onClick={handleCloseCall} disabled={!closeDisposition}>
            Close Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emergency Alert Dialog */}
      <Dialog open={!!emergencyAlert} maxWidth="sm" fullWidth
        slotProps={{ paper: { sx: { border: '4px solid #f44336', animation: 'emergBlink 0.5s infinite alternate', '@keyframes emergBlink': { from: { boxShadow: '0 0 20px #f44336' }, to: { boxShadow: '0 0 60px #f44336' } } } } }}>
        <DialogTitle sx={{ textAlign: 'center', bgcolor: '#f44336', color: '#fff', fontWeight: 700, fontSize: 24 }}>
          ⚠️ UNIT EMERGENCY ⚠️
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#f44336', mb: 2 }}>
            {emergencyAlert?.callsign}
          </Typography>
          <Typography variant="h6">has declared an emergency</Typography>
          {emergencyAlert?.personnel && (
            <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
              Personnel: {emergencyAlert.personnel}
            </Typography>
          )}
          {emergencyAlert?.location_address && (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 700, fontSize: 18 }}>
              📍 {emergencyAlert.location_address}
            </Typography>
          )}
          {emergencyAlert?.location_lat && emergencyAlert?.location_lng && (
            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
              {emergencyAlert.location_lat.toFixed(5)}, {emergencyAlert.location_lng.toFixed(5)}
              {emergencyAlert.location_updated_at && (
                <Box component="span" sx={{ ml: 1 }}>
                  (as of {formatTime24(emergencyAlert.location_updated_at)})
                </Box>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" size="large" onClick={() => setEmergencyAlert(null)}>
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
